import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";
import { auth, db, functions, googleProvider, microsoftProvider } from "@/js/firebase";
import {
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signInWithPopup,
  linkWithPopup,
  unlink,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from "firebase/auth";
import {
  doc,
  updateDoc,
  onSnapshot,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useRouter } from "vue-router";
import { useViewModeStore } from "@/stores/viewModeStore";
import { logger } from '../services/loggerService';
import { userService } from '../services/userService';
import { defaultUserRoute } from '@/utils/userRouting';

export const useUserAuthStore = defineStore("userAuth", () => {
  // State
  const user = ref({
    id: "",
    email: "",
    type: "", // "admin" or "user" (super consolidated into admin)
    systemRole: "", // "admin", "super", or "user" - controls permissions
    permissions: [], // Array of permission keys (e.g., ["projects", "calendar"])
    permissionGroupId: null, // Optional: ID of applied permission group
    company: "",
    companyId: "",
    name: "",
    avatar: "", // User avatar URL
    // Extended properties from Firestore (optional)
    companies: [], // Multi-company support
    activeCompanyId: "", // Currently active company
    profile: null, // Full profile from Firestore
    preferences: null, // User preferences from Firestore
    agencyId: null, // Agency staff: the agency's own company ID
    agencyRole: null // 'admin' | 'member' within that agency
  });
  const isLoading = ref(false);
  const authError = ref("");
  const allUsers = ref({ users: [] });
  const loadingAllUsers = ref(false);
  const initialized = ref(false);
  const codeSent = ref(false);
  const codeSentEmail = ref(""); // Track which email the code was sent to
  const isSubmitting = ref(false);
  const isAcceptingInvite = ref(false);
  const isVerifyingCode = ref(false);

  // Live subscription to the signed-in user's own document.
  //
  // The user doc is otherwise read once at sign-in, so anything written to it
  // afterwards was invisible until a manual refresh. That bites immediately for
  // agency staff: acceptInvite creates the user, and the applyAgencyInvite
  // trigger stamps agencyId a moment later — so a first sign-in showed no
  // agency bar and no switcher until the page was reloaded.
  let unsubscribeUserDoc = null;

  const stopWatchingUserDoc = () => {
    if (unsubscribeUserDoc) {
      unsubscribeUserDoc();
      unsubscribeUserDoc = null;
    }
  };

  // Patches only the fields that can change under the user while they are
  // signed in. Deliberately not a full re-map: identity and auth-derived values
  // belong to the auth handler.
  const watchUserDoc = (uid) => {
    stopWatchingUserDoc();
    if (!uid) return;

    unsubscribeUserDoc = onSnapshot(
      doc(db, "users", uid),
      (snap) => {
        if (!snap.exists() || !user.value.id) return;
        const data = snap.data();


        user.value = {
          ...user.value,
          companies: data.companies || user.value.companies,
          permissions: data.permissions || user.value.permissions,
          agencyId: data.agencyId || null,
          agencyRole: data.agencyRole || null,
          agencyAllCompanies: !!data.agencyAllCompanies,
        };
      },
      (error) => {
        logger.warn("[Auth] User document subscription failed", {
          uid,
          error: error.message,
        });
      }
    );
  };

  // The onAuthStateChanged handler, captured so refreshUser() can re-run it.
  let authStateHandler = null;
  
  // Router for navigation after auth actions
  const router = useRouter();
  
  // Getters
  const isLoggedIn = computed(() => !!user.value.id);
  const isAdmin = computed(() => user.value.type === "admin");
  // Deprecated: isSuper kept for backward compatibility, always returns isAdmin
  const isSuper = computed(() => user.value.type === "admin");
  // Check if user is super (bypasses all permission checks)
  const isSuperAdmin = computed(() => user.value.systemRole === "super");
  // Agency staff are ordinary 'user' accounts; this only says they belong to one.
  const isAgencyUser = computed(() => !!user.value.agencyId);
  /**
   * May run the agency: manage its team, its markup, and who reaches which
   * company. Anthem staff qualify too — they administer every agency.
   *
   * A scoped member is NOT this, and the Team and Settings screens are hidden
   * from them entirely rather than shown read-only: both exist to change things
   * they may not change, and Team also lists colleagues' access.
   */
  const isAgencyAdmin = computed(
    () => user.value.agencyRole === 'admin' || user.value.type === 'admin',
  );
  
  // Get the current active company (for multi-company support)
  const activeCompany = computed(() => {
    if (!user.value.companies?.length) return null;
    const activeId = user.value.activeCompanyId || user.value.companyId;
    return user.value.companies.find(c => c.companyId === activeId) || user.value.companies[0];
  });

  // Get notification preferences with defaults
  const notificationPreferences = computed(() => {
    const defaultTypes = {
      deliverable_assigned: true,
      deliverable_unassigned: true,
      deliverable_status_changed: true,
      project_in_review: true,
      agency_review_needed: true,
      comment_mention: true,
      comment_new: true,
      calendar_event_added: true,
      calendar_event_removed: true,
      calendar_reminder_week: true,
      calendar_reminder_day: true,
      client_date_selection_needed: true,
      client_date_selected: true
    };

    const defaults = {
      email: {
        enabled: true,
        frequency: 'instant', // 'instant' | 'digest' | 'none'
        types: { ...defaultTypes }
      },
      googleChat: {
        enabled: false,
        spaceId: null,
        googleEmail: null,
        linkedAt: null,
        types: { ...defaultTypes }
      },
      browser: {
        enabled: true,  // In-app notifications enabled by default
        types: { ...defaultTypes }
      },
      types: { ...defaultTypes }
    };

    // Merge user preferences with defaults (check both locations for backwards compatibility)
    const userPrefs = user.value.notificationPreferences || user.value.preferences?.notificationPreferences;
    if (!userPrefs) return defaults;

    return {
      email: {
        ...defaults.email,
        ...userPrefs.email,
        types: { ...defaults.email.types, ...userPrefs.email?.types }
      },
      googleChat: {
        ...defaults.googleChat,
        ...userPrefs.googleChat,
        types: { ...defaults.googleChat.types, ...userPrefs.googleChat?.types }
      },
      browser: {
        ...defaults.browser,
        ...userPrefs.browser,
        types: { ...defaults.browser.types, ...userPrefs.browser?.types }
      },
      types: { ...defaults.types, ...userPrefs.types }
    };
  });

  const defaultRedirect = computed(() => {
    // Admins have a configurable landing page — one owner for that decision.
    if (isAdmin.value) {
      return defaultUserRoute(user.value);
    }
    
    // Check if user has multiple companies - if so, redirect to account selector
    if (user.value.companies && user.value.companies.length > 1 && !user.value.activeCompanyId) {
      return { name: 'account-selector' };
    }
    
    // For single company users or users with explicitly selected activeCompanyId
    const companyId = user.value.activeCompanyId || user.value.companyId;
    return companyId ? {
      name: 'account-assets',
      params: { id: companyId }
    } : { name: 'login' };
  });
  
  // Persist active company to localStorage
  watch(() => user.value.activeCompanyId, (newCompanyId) => {
    if (newCompanyId && user.value.id) {
      localStorage.setItem(`activeCompany_${user.value.id}`, newCompanyId);
    }
  });
  
  // Actions
  /**
   * Initialize the auth store and set up auth state listener.
   * Resolves only after the first onAuthStateChanged callback completes
   * (so user.permissions is populated before the router permission check runs).
   */
  async function init() {
    if (initialized.value) return;

    logger.info('[Auth] Initializing authentication');
    isLoading.value = true;

    let firstFireResolve;
    const firstFire = new Promise((resolve) => { firstFireResolve = resolve; });
    const signalFirstFire = () => {
      if (firstFireResolve) {
        firstFireResolve();
        firstFireResolve = null;
      }
    };

    try {
      authStateHandler = async (firebaseUser) => {
        if (firebaseUser) {
          try {
            let { claims } = await firebaseUser.getIdTokenResult();
            
            logger.info('[Auth] Starting authentication process', {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              claims: {
                usertype: claims.usertype,
                company: claims.company,
                companyId: claims.companyId
              }
            });
            
            // Try to get user from Firestore. On cold page loads the Firestore
            // client occasionally fires the request before the auth token is
            // attached, returning a transient permission-denied. Retry once
            // after forcing a token refresh before falling through.
            let firestoreUser = null;
            let firestoreLoadError = null;
            try {
              logger.info('[Auth] Attempting to load user from Firestore', {
                uid: firebaseUser.uid
              });
              firestoreUser = await userService.getUser(firebaseUser.uid);
              logger.info('[Auth] Successfully loaded user from Firestore', {
                uid: firebaseUser.uid,
                hasCompanies: !!(firestoreUser?.companies),
                companiesCount: firestoreUser?.companies?.length || 0
              });
            } catch (firstErr) {
              firestoreLoadError = firstErr;
              if (firstErr?.code === 'permission-denied') {
                logger.warn('[Auth] First Firestore read denied — refreshing token and retrying', {
                  uid: firebaseUser.uid
                });
                try {
                  await firebaseUser.getIdToken(true);
                  firestoreUser = await userService.getUser(firebaseUser.uid);
                  firestoreLoadError = null;
                  logger.info('[Auth] Firestore read succeeded after token refresh', {
                    uid: firebaseUser.uid
                  });
                } catch (retryError) {
                  firestoreLoadError = retryError;
                  logger.warn('[Auth] Retry after token refresh also failed', {
                    uid: firebaseUser.uid,
                    errorCode: retryError.code,
                    errorMessage: retryError.message
                  });
                }
              }
            }

            // The agency portal renders off the user DOC, but firestore.rules
            // authorizes its queries off the CLAIM. When the two disagree the
            // portal loads and every query inside it is denied — silently, as an
            // empty list. A token minted before the claim landed is the common
            // cause, so refresh once and re-read.
            // agencyAllCompanies is checked alongside agencyId: it is what the
            // projects read rule uses to separate an agency admin (whole
            // roster) from a scoped member, so a stale one costs an admin their
            // whole review queue.
            const agencyClaimStale =
              firestoreUser &&
              ((firestoreUser.agencyId || null) !== (claims.agencyId || null) ||
                !!firestoreUser.agencyAllCompanies !== !!claims.agencyAllCompanies);

            if (agencyClaimStale) {
              logger.warn('[Auth] Agency claims out of sync with user doc — refreshing token', {
                uid: firebaseUser.uid,
                docAgencyId: firestoreUser.agencyId || null,
                claimAgencyId: claims.agencyId || null,
                docAllCompanies: !!firestoreUser.agencyAllCompanies,
                claimAllCompanies: !!claims.agencyAllCompanies
              });
              ({ claims } = await firebaseUser.getIdTokenResult(true));
            }

            if (!firestoreUser) {
              logger.warn('[Auth] User not found in Firestore, using custom claims', {
                uid: firebaseUser.uid,
                errorCode: firestoreLoadError?.code,
                errorMessage: firestoreLoadError?.message
              });

              // If it's a permissions error, the user exists but we can't read it
              // This might happen with multi-company users due to security rules
              if (firestoreLoadError?.code === 'permission-denied') {
                logger.warn('[Auth] Permissions denied for user document - user may have multiple companies but security rules prevent access', {
                  uid: firebaseUser.uid,
                  suggestion: 'User should be redirected to AccountSelector to handle multi-company scenario'
                });
                
                // Create a minimal user object that will force AccountSelector navigation
                // by having an empty companies array but signaling this is a multi-company user
                firestoreUser = {
                  id: firebaseUser.uid,
                  profile: {
                    email: firebaseUser.email,
                    firstName: firebaseUser.displayName?.split(' ')[0] || '',
                    lastName: firebaseUser.displayName?.split(' ')[1] || ''
                  },
                  companies: [], // Empty array will trigger migration or AccountSelector
                  systemRole: claims.usertype || 'user',
                  preferences: {
                    // Add a flag to indicate this user might have multiple companies
                    requiresAccountSelection: true
                  }
                };
              }
            }
            
            if (firestoreUser) {
              // Check if user has companies or needs migration from custom claims
              let companiesArray = firestoreUser.companies || [];

              // Normalize companies array - convert string format to object format
              if (companiesArray && companiesArray.length > 0) {
                companiesArray = companiesArray.map(company => {
                  if (typeof company === 'string') {
                    // Old format - convert string to object
                    logger.info('[Auth] Converting legacy company format', {
                      uid: firebaseUser.uid,
                      companyId: company
                    });
                    return {
                      companyId: company,
                      companyName: '', // Will be populated later if needed
                      role: 'user',
                      isPrimary: false,
                      status: 'active'
                    };
                  }
                  return company; // Already in correct format
                });
              }

              // If user has empty companies array but custom claims have company data, migrate it
              if ((!companiesArray || companiesArray.length === 0) && claims.companyId) {
                logger.info('[Auth] Migrating user with empty companies array from custom claims', {
                  uid: firebaseUser.uid,
                  claimsCompanyId: claims.companyId,
                  claimsCompany: claims.company
                });
                
                companiesArray = [{
                  companyId: claims.companyId,
                  companyName: claims.company || '',
                  role: 'user',
                  isPrimary: true,
                  status: 'active'
                }];
                
                // In-memory only: client users can't write their own companies[]
                // (firestore.rules). Membership changes go through manageCompanyMember.
              }
              
              // Use Firestore data (new system)
              const primaryCompany = companiesArray?.find(c => c.isPrimary) || companiesArray?.[0];

              let activeCompanyId = '';
              let companyId = '';

              // Simple logic: Only set IDs for single-company users
              if (companiesArray && companiesArray.length === 1) {
                // Single company user - set both IDs to the same company
                // Handle both string and object format (after normalization, should all be objects)
                const singleCompanyId = primaryCompany?.companyId || firestoreUser.preferences?.defaultCompanyId || claims.companyId || '';
                activeCompanyId = singleCompanyId;
                companyId = singleCompanyId;
              } else if (companiesArray && companiesArray.length > 1) {
                // Multi-company user - NO default IDs, always force account selector
                activeCompanyId = '';
                companyId = '';
              } else {
                // No companies or legacy - use fallback for backward compatibility
                const fallbackId = primaryCompany?.companyId || firestoreUser.preferences?.defaultCompanyId || claims.companyId || '';
                activeCompanyId = fallbackId;
                companyId = fallbackId;
              }
              
              // Determine systemRole and type
              // systemRole: 'super', 'admin', or 'user' (stored in Firestore and claims)
              // type: 'admin' or 'user' (for routing - super maps to admin)
              const systemRole = firestoreUser.systemRole || claims.usertype || 'user';
              const userType = (systemRole === 'super' || systemRole === 'admin') ? 'admin' : systemRole;


              user.value = {
                id: firebaseUser.uid,
                email: firestoreUser.profile?.email || firebaseUser.email,
                type: userType,
                systemRole: systemRole,
                permissions: firestoreUser.permissions || [],
                permissionGroupId: firestoreUser.permissionGroupId || null,
                company: companyId ? (primaryCompany?.companyName || claims.company || "") : "",
                companyId: companyId,
                name: `${firestoreUser.profile?.firstName || ''} ${firestoreUser.profile?.lastName || ''}`.trim() ||
                      firebaseUser.displayName || "",
                avatar: firestoreUser.profile?.avatar || "",
                // Extended properties
                companies: companiesArray,
                activeCompanyId,
                profile: firestoreUser.profile,
                preferences: firestoreUser.preferences,
                notificationPreferences: firestoreUser.notificationPreferences || {},
                timeOffApproverId: firestoreUser.timeOffApproverId || null,
                // Agency staff: agencyId is the agency's own company ID. Access to
                // client companies still lives in companies[] like any other user.
                agencyId: firestoreUser.agencyId || null,
                agencyRole: firestoreUser.agencyRole || null,
                agencyAllCompanies: !!firestoreUser.agencyAllCompanies,
                // Timestamps
                createdAt: firestoreUser.createdAt || firebaseUser.metadata?.creationTime || null,
                updatedAt: firestoreUser.updatedAt || firestoreUser.profile?.updatedAt || null
              };
              
              // Keep the agency fields (and companies) live from here on.
              watchUserDoc(firebaseUser.uid);

              logger.info('[Auth] User authenticated with Firestore data', {
                userId: firebaseUser.uid,
                userType: user.value.type,
                companiesCount: companiesArray?.length || 0,
                companies: companiesArray?.map(c => typeof c === 'string' ? c : c?.companyId) || [],
                activeCompanyId,
                hasLegacyCompanyId: !!claims.companyId
              });
              
            } else if (!claims.usertype && !isAcceptingInvite.value) {
              // No Firestore doc AND no custom claims AND not accepting invite — unknown user
              logger.warn('[Auth] Unknown user with no Firestore doc or claims, signing out', {
                uid: firebaseUser.uid,
                email: firebaseUser.email
              });
              authError.value = 'No account found. Please contact your administrator.';
              // Sign-in already created the Auth record; remove it so uninvited
              // Google/Microsoft sign-ins don't leave orphaned accounts. Fresh
              // credential, so no reauth needed. Deleting also signs out.
              try {
                await firebaseUser.delete();
              } catch (deleteError) {
                logger.warn('[Auth] Could not delete orphaned auth user, signing out', { uid: firebaseUser.uid, error: deleteError?.code });
                await signOut(auth);
              }
              return;
            } else {
              // Fallback to custom claims (backward compatibility for legacy users)
              const fallbackType = (claims.usertype === 'super' ? 'admin' : claims.usertype) || "user";
              user.value = {
                id: firebaseUser.uid,
                email: firebaseUser.email,
                type: fallbackType,
                systemRole: fallbackType,
                permissions: [],
                permissionGroupId: null,
                company: claims.company || "",
                companyId: claims.companyId || "",
                name: firebaseUser.displayName || "",
                avatar: "",
                companies: [],
                activeCompanyId: claims.companyId || "",
                profile: null,
                preferences: null,
                notificationPreferences: {}
              };

              // Attempt to migrate legacy user to Firestore.
              // Skipped mid-invite: acceptInvite creates the doc itself, and if we win
              // the race it takes the "existing user" path and never sets the usertype claim.
              if (!isAcceptingInvite.value) {
                try {
                  const migratedUser = await userService.createOrUpdateFromAuth(firebaseUser, claims);
                  if (migratedUser) {
                    logger.info('[Auth] User migrated to Firestore', { uid: firebaseUser.uid });
                  }
                } catch (error) {
                  logger.warn('[Auth] Could not migrate user to Firestore', { error });
                }
              }
            }
            
            logger.info('[Auth] User authenticated', { 
              userId: user.value.id,
              userType: user.value.type,
              activeCompany: user.value.activeCompanyId
            });
          } catch (error) {
            logger.error('[Auth] Failed to process user data:', error);
            await signOut(auth);
            user.value = {
              id: "", email: "", type: "", systemRole: "", permissions: [], permissionGroupId: null,
              company: "", companyId: "", name: "", avatar: "",
              companies: [], activeCompanyId: "", profile: null, preferences: null
            };
          }
        } else {
          stopWatchingUserDoc();
          user.value = {
            id: "", email: "", type: "", systemRole: "", permissions: [], permissionGroupId: null,
            company: "", companyId: "", name: "", avatar: "",
            companies: [], activeCompanyId: "", profile: null, preferences: null,
            agencyId: null, agencyRole: null
          };
          logger.info('[Auth] User signed out');
        }

        isLoading.value = false;
        signalFirstFire();
      };

      onAuthStateChanged(auth, authStateHandler);

      initialized.value = true;
    } catch (error) {
      signalFirstFire();
      logger.error('[Auth] Initialization failed:', error);
      authError.value = error.message;
      isLoading.value = false;
    }

    // Block until the first auth state has been fully processed —
    // ensures user.permissions is populated before any caller proceeds.
    await firstFire;
  }

  /**
   * Re-run the auth-state handler against the current Firebase user.
   * Needed after claims/Firestore data change without a sign-in event
   * (e.g. accepting an invite via OAuth — the popup signs in before the
   * user doc and claims exist, and init() is a no-op once initialized).
   */
  async function refreshUser() {
    if (!authStateHandler || !auth.currentUser) return;
    await authStateHandler(auth.currentUser);
  }

  /**
   * Log in with email and password
   */
  async function loginWithEmail(email, password) {
    isLoading.value = true;
    authError.value = "";
    
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      logger.info('[Auth] User logged in successfully', { email });
      
      // Update last login timestamp for actual login events
      if (result.user?.uid) {
        await userService.updateLastLogin(result.user.uid);
      }
    } catch (error) {
      logger.error('[Auth] Login failed:', { error: error.message });
      authError.value = error.message;
      throw error;
    } finally {
      isLoading.value = false;
    }
  }
  
  /**
   * Log out the current user
   */
  async function logout() {
    try {
      // Clear active company from localStorage
      if (user.value.id) {
        localStorage.removeItem(`activeCompany_${user.value.id}`);
      }
      
      await signOut(auth);
      logger.info('[Auth] User logged out successfully');
      router.push('/login');
    } catch (error) {
      logger.error('[Auth] Logout failed:', error);
      authError.value = error.message;
    }
  }
  
  /**
   * Switch active company for multi-company users
   */
  async function switchCompany(companyId) {
    if (!user.value.id) {
      logger.error('[Auth] User must be logged in to switch companies');
      throw new Error("User must be logged in");
    }
    
    // Verify user has access to this company
    const hasAccess = user.value.companies?.some(c => c.companyId === companyId);
    if (!hasAccess) {
      logger.error('[Auth] User does not have access to company', { companyId });
      throw new Error("Access denied to this company");
    }
    
    try {
      // Update in Firestore
      await userService.setActiveCompany(user.value.id, companyId);
      
      // Update local state
      const company = user.value.companies.find(c => c.companyId === companyId);
      user.value.activeCompanyId = companyId;
      user.value.companyId = companyId;
      user.value.company = company?.companyName || "";
      
      // Update view mode store if needed
      const viewModeStore = useViewModeStore();
      if (viewModeStore.isImpersonating) {
        await viewModeStore.endImpersonation();
      }
      
      logger.info('[Auth] Switched company', { companyId });
      
      // Navigate to the new company's assets page
      router.push({
        name: 'account-assets',
        params: { id: companyId }
      });
    } catch (error) {
      logger.error('[Auth] Failed to switch company:', error);
      throw error;
    }
  }
  
  /**
   * Send password reset email
   */
  async function resetPassword(email) {
    isLoading.value = true;
    authError.value = "";
    
    try {
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (error) {
      logger.error('[Auth] Password reset error:', error);
      authError.value = error.message;
      throw error;
    } finally {
      isLoading.value = false;
    }
  }
  
  /**
   * Handle email link sign in
   */
  /**
   * Send a 6-digit verification code to the user's email
   */
  async function sendAuthCode(email) {
    isSubmitting.value = true;
    authError.value = "";

    try {
      const sendAuthCodeFn = httpsCallable(functions, 'sendAuthCode');
      const result = await sendAuthCodeFn({ email });

      if (result.data.inviteToken) {
        // Account doesn't exist yet — they have an unaccepted invite. No code was
        // sent; the caller routes them to the invite page.
        logger.info('[Auth] Email belongs to a pending invite', { email });
        return { inviteToken: result.data.inviteToken };
      } else if (result.data.success) {
        codeSent.value = true;
        codeSentEmail.value = email;
        logger.info('[Auth] Auth code sent successfully', { email });
      } else {
        throw new Error(result.data.error);
      }
    } catch (error) {
      // Rate-limit rejections are expected backend throttling, not app failures
      logger[/too many/i.test(error.message) ? 'warn' : 'error']('[Auth] Failed to send auth code:', error);
      // Extract the user-facing message from Firebase callable errors
      authError.value = error.message || 'Failed to send verification code.';
      throw error;
    } finally {
      isSubmitting.value = false;
    }
  }

  /**
   * Verify a 6-digit code and sign in with the returned custom token
   */
  async function verifyAuthCode(email, code) {
    isVerifyingCode.value = true;
    authError.value = "";

    try {
      const verifyAuthCodeFn = httpsCallable(functions, 'verifyAuthCode');
      const result = await verifyAuthCodeFn({ email, code });

      if (result.data.success && result.data.token) {
        // Sign in with the custom token from the server
        const userCredential = await signInWithCustomToken(auth, result.data.token);
        logger.info('[Auth] Code verification and sign-in successful');

        // Update last login timestamp
        if (userCredential.user?.uid) {
          await userService.updateLastLogin(userCredential.user.uid);
        }

        // Reset code state
        codeSent.value = false;
        codeSentEmail.value = "";

        // Don't redirect here - let onAuthStateChanged handle it
      } else {
        throw new Error('Verification failed.');
      }
    } catch (error) {
      // An incorrect/expired login code is expected user input, not a system
      // error — log at warn so it stays out of the persisted error log.
      // Genuine failures (network, backend) keep error level.
      const benign = /incorrect code|attempts remaining|expired|invalid/i.test(error?.message || '');
      if (benign) {
        logger.warn('[Auth] Code verification failed:', error.message);
      } else {
        logger.error('[Auth] Code verification failed:', error);
      }
      authError.value = error.message || 'Verification failed. Please try again.';
      throw error;
    } finally {
      isVerifyingCode.value = false;
    }
  }

  /**
   * Reset code entry state (go back to email input)
   */
  function resetCodeState() {
    codeSent.value = false;
    codeSentEmail.value = "";
    authError.value = "";
  }

  /**
   * Sign in with Google OAuth
   * Only works for existing users — unknown users are rejected in init()
   */
  async function signInWithGoogle() {
    isSubmitting.value = true;
    authError.value = "";

    try {
      await signInWithPopup(auth, googleProvider);
      logger.info('[Auth] Google sign-in popup completed');
      // onAuthStateChanged in init() handles the rest
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
        // User cancelled — not an error
        logger.debug('[Auth] Google sign-in popup closed by user');
        return;
      }
      logger.error('[Auth] Google sign-in failed:', error);
      authError.value = error.code === 'auth/account-exists-with-different-credential'
        ? 'An account already exists with this email using a different sign-in method.'
        : 'Google sign-in failed. Please try again.';
    } finally {
      isSubmitting.value = false;
    }
  }

  /**
   * Sign in with Microsoft OAuth
   * Only works for existing users — unknown users are rejected in init()
   */
  async function signInWithMicrosoft() {
    isSubmitting.value = true;
    authError.value = "";

    try {
      await signInWithPopup(auth, microsoftProvider);
      logger.info('[Auth] Microsoft sign-in popup completed');
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
        logger.debug('[Auth] Microsoft sign-in popup closed by user');
        return;
      }
      logger.warn('[Auth] Microsoft sign-in failed:', error);
      authError.value = error.code === 'auth/account-exists-with-different-credential'
        ? 'An account already exists with this email using a different sign-in method.'
        : 'Microsoft sign-in failed. Please try again.';
    } finally {
      isSubmitting.value = false;
    }
  }

  /**
   * Link a Google account to the current user's Firebase Auth profile
   */
  async function linkGoogle() {
    try {
      const result = await linkWithPopup(auth.currentUser, googleProvider);
      const providerData = result.user.providerData.find(p => p.providerId === 'google.com');

      await updateDoc(doc(db, 'users', user.value.id), {
        'profile.linkedProviders.google.com': {
          email: providerData?.email || '',
          displayName: providerData?.displayName || '',
          linkedAt: new Date()
        }
      });

      logger.info('[Auth] Google account linked');
      return { success: true, providerData };
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
        logger.debug('[Auth] Google linking popup closed by user');
      } else {
        logger.error('[Auth] Failed to link Google:', error);
      }
      throw error;
    }
  }

  /**
   * Link a Microsoft account to the current user's Firebase Auth profile
   */
  async function linkMicrosoft() {
    try {
      const result = await linkWithPopup(auth.currentUser, microsoftProvider);
      const providerData = result.user.providerData.find(p => p.providerId === 'microsoft.com');

      await updateDoc(doc(db, 'users', user.value.id), {
        'profile.linkedProviders.microsoft.com': {
          email: providerData?.email || '',
          displayName: providerData?.displayName || '',
          linkedAt: new Date()
        }
      });

      logger.info('[Auth] Microsoft account linked');
      return { success: true, providerData };
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
        logger.debug('[Auth] Microsoft linking popup closed by user');
      } else {
        logger.error('[Auth] Failed to link Microsoft:', error);
      }
      throw error;
    }
  }

  /**
   * Unlink a provider from the current user's Firebase Auth profile
   * @param {string} providerId - e.g. 'google.com' or 'microsoft.com'
   */
  async function unlinkProvider(providerId) {
    try {
      await unlink(auth.currentUser, providerId);

      await updateDoc(doc(db, 'users', user.value.id), {
        [`profile.linkedProviders.${providerId}`]: null
      });

      logger.info('[Auth] Provider unlinked', { providerId });
      return { success: true };
    } catch (error) {
      logger.error('[Auth] Failed to unlink provider:', error);
      throw error;
    }
  }

  /**
   * List all users (admin only)
   */
  async function listAllUsers() {
    if (!isAdmin.value) {
      logger.error('[Auth] Unauthorized attempt to list users');
      return [];
    }
    
    loadingAllUsers.value = true;
    
    try {
      const listUsersFunction = httpsCallable(functions, 'listUsers');
      // One retry on infra-level failures. Bare `internal` is also what the SDK
      // reports when the request never left the browser (Oct 17: laptop waking,
      // network not up — no invocation server-side), so back off before retrying.
      const result = await listUsersFunction().catch(async (err) => {
        if (!['functions/internal', 'functions/unavailable', 'functions/deadline-exceeded'].includes(err.code)) throw err;
        logger.warn('[Auth] listUsers failed, retrying once', { code: err.code, online: navigator.onLine });
        await new Promise((r) => setTimeout(r, 3000));
        return listUsersFunction();
      });

      if (result.data?.users) {
        allUsers.value = result.data;
      } else if (Array.isArray(result.data)) {
        allUsers.value = { users: result.data };
      } else {
        logger.error('[Auth] Invalid users list response format');
        allUsers.value = { users: [] };
      }
      
      logger.info('[Auth] Users list retrieved', { count: allUsers.value.users.length });
      return allUsers.value;
    } catch (error) {
      logger.error('[Auth] Failed to list users:', error);
      allUsers.value = { users: [] };
      throw error;
    } finally {
      loadingAllUsers.value = false;
    }
  }
  
  /**
   * Add a new user (admin only) - Enhanced with Firestore multi-company support
   */
  async function addUser(userData) {
    if (!isAdmin.value) {
      logger.error('[Auth] Only admins can add users');
      throw new Error("Only admins can add users");
    }
    
    loadingAllUsers.value = true;
    try {
      // Check if user exists in Firestore by email
      const existingUser = await userService.getUserByEmail(userData.email);
      
      if (existingUser) {
        // User exists - add company to their profile
        logger.info('[Auth] User exists, adding company', { email: userData.email });
        
        await userService.addCompany(existingUser.id, {
          companyId: userData.companyId,
          companyName: userData.company,
          role: 'user',
          isPrimary: existingUser.companies?.length === 0
        });
        
        // Send notification email about new company access
        const welcomeEmail = httpsCallable(functions, 'welcomeEmail');
        await welcomeEmail({ 
          email: userData.email,
          type: 'company_added',
          companyName: userData.company
        });
        
      } else {
        // Create new user in Firebase Auth
        const addUserFunction = httpsCallable(functions, "addUser");
        const setUserType = httpsCallable(functions, "setUserType");
        const welcomeEmail = httpsCallable(functions, "welcomeEmail");

        const userResult = await addUserFunction(userData);
        const uid = userResult.data.uid;

        // Set custom claims for backward compatibility
        const typeData = {
          uid: uid,
          type: userData.type || 'user'
        };

        // Only add company fields if they exist
        if (userData.company) typeData.company = userData.company;
        if (userData.companyId) typeData.companyId = userData.companyId;

        await setUserType(typeData);
        
        // Create user in Firestore
        const companiesArray = userData.companyId ? [{
          companyId: userData.companyId,
          companyName: userData.company || '',
          role: 'user',
          joinedAt: new Date(),
          isPrimary: true,
          status: 'active'
        }] : [];

        await userService.createUser(uid, {
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          email: userData.email,
          companies: companiesArray,
          systemRole: userData.type || 'user',
          defaultCompanyId: userData.companyId || null,
          createdBy: user.value.id,
          invitedBy: user.value.id
        });
        
        // Send welcome email
        await welcomeEmail({ email: userData.email });
      }
      
      await listAllUsers();
      return { success: true };
    } catch (error) {
      logger.error('[Auth] Error adding user:', error);
      throw error;
    } finally {
      loadingAllUsers.value = false;
    }
  }
  
  // Removed checkUserExists and addCompanyToUser - now handled directly through userService
  
  /**
   * Update a user (admin only)
   */
  async function updateUser(uid, updates) {
    if (!isAdmin.value) {
      logger.error('[Auth] Only admins can update users');
      throw new Error("Only admins can update users");
    }
    
    try {
      logger.info('[Auth] Updating user', { uid, updates });
      
      // Use Firebase Admin SDK through a function to update user
      const updateUserFunction = httpsCallable(functions, "updateUser");
      const result = await updateUserFunction({ uid, ...updates });
      
      // Refresh the users list
      await listAllUsers();
      
      logger.info('[Auth] User updated successfully');
      return result.data;
    } catch (error) {
      logger.error('[Auth] Error updating user:', error);
      // Re-throw with a more user-friendly message if it's a known error
      if (error.code === 'functions/already-exists') {
        throw new Error('Email address is already in use');
      }
      throw error;
    }
  }

  /**
   * Delete a user (admin only)
   */
  async function deleteUser(uid, reassignTo = null) {
    if (!isAdmin.value) {
      logger.error('[Auth] Only admins can delete users');
      throw new Error("Only admins can delete users");
    }
    
    loadingAllUsers.value = true;
    try {
      const deleteUserFunction = httpsCallable(functions, "deleteUser");
      const result = await deleteUserFunction({ user: { uid: uid }, reassignTo });
      await listAllUsers();
      return result.data;
    } catch (error) {
      logger.error('[Auth] Error deleting user:', error);
      throw error;
    } finally {
      loadingAllUsers.value = false;
    }
  }
  
  /**
   * Refresh users list (admin only)
   * Use this when you need to force a refresh of the users list
   */
  async function refreshUsers() {
    if (!isAdmin.value) {
      logger.error('[Auth] Only admins can refresh users');
      throw new Error("Only admins can refresh users");
    }
    return listAllUsers();
  }
  
  /**
   * Update user profile
   */
  async function updateUserProfile(profileData) {
    if (!user.value.id) {
      logger.error('[Auth] User must be logged in to update profile');
      throw new Error("User must be logged in to update profile");
    }

    try {
      logger.info('[Auth] Updating user profile', { uid: user.value.id });

      // Write directly to Firestore - rules allow users to update their own document
      const userDocRef = doc(db, 'users', user.value.id);

      // Build the update object with profile fields
      const now = new Date().toISOString();
      const updateData = {
        'profile.updatedAt': now,
        updatedAt: now
      };

      // Map profile fields
      if (profileData.firstName !== undefined) {
        updateData['profile.firstName'] = profileData.firstName;
      }
      if (profileData.lastName !== undefined) {
        updateData['profile.lastName'] = profileData.lastName;
      }
      if (profileData.name !== undefined) {
        updateData.name = profileData.name;
      }
      if (profileData.email !== undefined) {
        updateData['profile.email'] = profileData.email;
      }
      if (profileData.displayName !== undefined) {
        updateData['profile.displayName'] = profileData.displayName;
        updateData.displayName = profileData.displayName;
      }
      if (profileData.avatar !== undefined) {
        updateData['profile.avatar'] = profileData.avatar;
        updateData.avatar = profileData.avatar;
      }
      if (profileData.avatarPath !== undefined) {
        // Raw Bunny path for re-sign-on-read (the signed `avatar` URL expires).
        updateData['profile.avatarPath'] = profileData.avatarPath;
        updateData.avatarPath = profileData.avatarPath;
      }
      if (profileData.phone !== undefined) {
        updateData['profile.phone'] = profileData.phone;
      }
      if (profileData.title !== undefined) {
        updateData['profile.title'] = profileData.title;
      }

      await updateDoc(userDocRef, updateData);
      logger.debug('[Auth] Profile saved directly to Firestore');

      // Update local user state - make sure to update both the direct fields and the profile object
      user.value = {
        ...user.value,
        ...profileData,
        updatedAt: now,
        profile: {
          ...user.value.profile,
          ...profileData,
          updatedAt: now
        }
      };

      logger.info('[Auth] User profile updated successfully', { userId: user.value.id });

      return true;
    } catch (error) {
      logger.error('[Auth] Error updating user profile:', error);
      throw error;
    }
  }

  /**
   * Update local user preferences without Firestore call
   * Used when Firestore is already updated elsewhere (e.g., push notification service)
   * @param {Object} updates - Object with dot-notation keys (e.g., 'notificationPreferences.browser.enabled': true)
   */
  function updateLocalUserPreferences(updates) {
    if (!user.value) return;

    // Deep clone the user object
    const updatedUser = JSON.parse(JSON.stringify(user.value));

    // Apply updates using dot notation
    for (const [path, value] of Object.entries(updates)) {
      const keys = path.split('.');
      let current = updatedUser;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
    }

    user.value = updatedUser;
    logger.info('[Auth] Local user preferences updated', { updates });
  }

  /**
   * Update a specific notification preference
   * @param {string} path - Dot-notation path within notificationPreferences (e.g., 'types.deliverable_assigned')
   * @param {any} value - The new value to set
   */
  async function updateNotificationPreference(path, value) {
    if (!user.value.id) {
      logger.error('[Auth] User must be logged in to update notification preferences');
      throw new Error('User must be logged in to update notification preferences');
    }

    try {
      logger.info('[Auth] Updating notification preference', { path, value });

      // Build the Firestore field path
      const firestorePath = `notificationPreferences.${path}`;

      // Update Firestore directly
      const userDocRef = doc(db, 'users', user.value.id);
      await updateDoc(userDocRef, {
        [firestorePath]: value
      });

      // Update local state
      const keys = path.split('.');
      const updatedPrefs = { ...user.value.notificationPreferences };

      // Navigate to the nested property and set value
      let current = updatedPrefs;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;


      user.value = {
        ...user.value,
        notificationPreferences: updatedPrefs
      };

      logger.info('[Auth] Notification preference updated successfully', { path, value });
    } catch (error) {
      logger.error('[Auth] Failed to update notification preference:', error);
      throw error;
    }
  }

  /**
   * Update a single user preference (e.g. 'defaultLandingPage').
   * @param {string} path - Dot-notation path within `preferences`
   * @param {any} value
   */
  async function updateUserPreference(path, value) {
    if (!user.value?.id) {
      throw new Error('User must be logged in to update preferences');
    }

    const userDocRef = doc(db, 'users', user.value.id);
    await updateDoc(userDocRef, { [`preferences.${path}`]: value });

    updateLocalUserPreferences({ [`preferences.${path}`]: value });
    logger.info('[Auth] User preference updated', { path, value });
  }

  /**
   * Check if the current user has seen a specific tour
   * @param {string} tourId - Tour identifier (e.g. 'reviewDetail')
   * @returns {boolean}
   */
  function hasSeenTour(tourId) {
    return !!user.value?.preferences?.toursSeen?.[tourId];
  }

  /**
   * Mark a tour as seen for the current user
   * Updates Firestore and local state so the UI reflects immediately
   * @param {string} tourId - Tour identifier (e.g. 'reviewDetail')
   */
  async function markTourSeen(tourId) {
    if (!user.value.id) return;
    try {
      await userService.markTourSeen(user.value.id, tourId);
      // Update local state immediately so the UI doesn't re-trigger the tour
      user.value = {
        ...user.value,
        preferences: {
          ...user.value.preferences,
          toursSeen: {
            ...user.value.preferences?.toursSeen,
            [tourId]: new Date(),
          },
        },
      };
    } catch (error) {
      logger.error('[Auth] Failed to mark tour as seen:', error);
    }
  }

  return {
    // State
    user,
    isLoading,
    authError,
    allUsers,
    loadingAllUsers,
    initialized,
    codeSent,
    codeSentEmail,
    isSubmitting,
    isAcceptingInvite,
    isVerifyingCode,

    // Getters
    isLoggedIn,
    isAdmin,
    isSuper,
    isSuperAdmin,
    isAgencyUser,
    isAgencyAdmin,
    defaultRedirect,
    activeCompany,
    notificationPreferences,

    // Actions
    init,
    refreshUser,
    loginWithEmail,
    logout,
    resetPassword,
    sendAuthCode,
    verifyAuthCode,
    resetCodeState,
    signInWithGoogle,
    signInWithMicrosoft,
    linkGoogle,
    linkMicrosoft,
    unlinkProvider,
    listAllUsers,
    addUser,
    updateUser,
    deleteUser,
    updateUserProfile,
    updateLocalUserPreferences,
    updateNotificationPreference,
    updateUserPreference,
    refreshUsers,
    switchCompany,
    hasSeenTour,
    markTourSeen
  };
}); 